import type { PaigeClient } from '@paige/api-client';
import type { SessionApi } from './data-source';
import { unwrap } from './http';

/** A sign-in's body: the token, and only when there is one, the mailed code and the invitation's company. */
const sessionBody = (idToken: string, emailCode?: string, invitation?: string) => ({
  idToken,
  ...(emailCode ? { emailCode } : {}),
  ...(invitation ? { invitation } : {}),
});

/** The invitations waiting for the person signed in, and their answer to each. */
function invitationCalls(
  client: PaigeClient,
): Pick<SessionApi, 'invitations' | 'acceptInvitation' | 'declineInvitation'> {
  return {
    async invitations() {
      return unwrap(await client.GET('/me/invitations'));
    },
    async acceptInvitation(id) {
      const params = { path: { companyId: id } };
      return unwrap(await client.POST('/me/invitations/{companyId}/accept', { params }));
    },
    async declineInvitation(id) {
      const params = { path: { companyId: id } };
      unwrap(await client.POST('/me/invitations/{companyId}/decline', { params }));
    },
  };
}

/** The session: ID token in, `__Host-paige_session` cookie out; `/me` afterwards. */
export function sessionApi(client: PaigeClient): SessionApi {
  return {
    async getMe() {
      return unwrap(await client.GET('/me'));
    },
    async create(idToken, emailCode, invitation) {
      const body = sessionBody(idToken, emailCode, invitation);
      return unwrap(await client.POST('/auth/session', { body }));
    },
    ...invitationCalls(client),
    async confirmEmail(idToken, emailCode) {
      unwrap(await client.POST('/auth/email-confirm', { body: { idToken, emailCode } }));
    },
    async sendEmailCode(idToken) {
      unwrap(await client.POST('/auth/email-code', { body: { idToken } }));
    },
    async requestPasswordReset(email) {
      unwrap(await client.POST('/auth/password-reset', { body: { email } }));
    },
    async resetPassword(token, password) {
      unwrap(await client.POST('/auth/password-reset/confirm', { body: { token, password } }));
    },
    async signUp(idToken, company, acquisition, partnerCode) {
      return unwrap(
        await client.POST('/signup', {
          body: {
            idToken,
            company,
            acquisition: acquisition ?? null,
            partnerCode: partnerCode || null,
          },
        }),
      );
    },
    async destroy() {
      unwrap(await client.DELETE('/auth/session'));
    },
  };
}
